/**
 * DataTablePageScroll — table for pages where the PAGE owns vertical scroll
 * while the table scrolls only horizontally, AND the column header must pin to
 * the page (below collapsing chrome).
 *
 * The hard constraint: any non-`visible` overflow on the table's wrapper makes
 * that wrapper the sticky scroll-container, so a `<thead sticky>` would pin to
 * the wrapper (mid-table) instead of the page. And `overflow-x: auto` (needed
 * for horizontal scroll) forces `overflow-y: visible` → `auto`, so there's no
 * CSS-only way to keep horizontal scroll + page-pinned sticky header on the
 * same `<table>`.
 *
 * Solution: split header and body into two tables.
 *  - A page-level `position: sticky` header table that pins below the chrome.
 *    It has `overflow-x: hidden` and its `scrollLeft` is mirrored from the
 *    body's, so its columns track the body during horizontal scroll.
 *  - A body table inside the horizontally-scrolling box (just `<tbody>`).
 * Both tables use `table-layout: fixed` with the same explicit column widths,
 * so they line up perfectly.
 */
import * as React from 'react';

import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';

import type { TableVariants } from '@/components/data-table/data-table';
import { DataTableResizeHandle } from '@/components/data-table/data-table-resize-handle';
import { DataTableSortingArrows } from '@/components/data-table/data-table-sorting-arrows';
import { TableBody, TableCell, TableHead, TableRow } from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from '@/lib/utils';

import { buildRowMouseHandlers } from '../utils/row-navigation';

interface DataTablePageScrollProps<TData> extends React.ComponentProps<'div'> {
  table: TanstackTable<TData>;
  /** Rendered as the last row(s) — empty/loading/error states and the infinite-scroll sentinel. */
  lastRowChildren?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Detail-route href for a row — enables CMD/CTRL+click and middle-click "open in new tab". */
  getRowHref?: (row: TData) => string | undefined;
  /** Right-click on a row (e.g. to open a custom context menu). */
  onRowContextMenu?: (row: TData, event: React.MouseEvent) => void;
  getRowClassName?: (row: TData) => string | undefined;
  variant?: TableVariants;
  /** Unique table ID for enabling column resizing with localStorage persistence. */
  tableId?: string;
  /** Callback when column resize ends — used to persist column widths. */
  onResizeEnd?: () => void;
  /**
   * Number of trailing columns that share remaining horizontal space.
   * Defaults to 1.
   */
  fillLastColumns?: number;
  /**
   * Top offset (px) for the page-level sticky column header. The header pins
   * to the page below the collapsing chrome at this offset.
   */
  stickyHeaderTopPx?: number;
}

export function DataTablePageScroll<TData>({
  table,
  lastRowChildren,
  onRowClick,
  getRowHref,
  onRowContextMenu,
  getRowClassName,
  className,
  variant,
  tableId,
  onResizeEnd,
  fillLastColumns = 1,
  stickyHeaderTopPx = 0,
  ...props
}: DataTablePageScrollProps<TData>) {
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);
  const headerScrollRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const hasDistributedInitialWidth = React.useRef(false);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container || !tableId) return;

    const updateContainerWidth = () => {
      const newContainerWidth = container.clientWidth;
      setContainerWidth(prev => (prev !== newContainerWidth ? newContainerWidth : prev));

      if (!hasDistributedInitialWidth.current && newContainerWidth > 0) {
        hasDistributedInitialWidth.current = true;
        const columnSizingState = table.getState().columnSizing;
        const hasCustomSizing = Object.keys(columnSizingState).length > 0;

        if (!hasCustomSizing) {
          const visibleColumns = table.getVisibleLeafColumns();
          const totalDefaultWidth = visibleColumns.reduce((sum, col) => sum + col.getSize(), 0);
          if (newContainerWidth > totalDefaultWidth) {
            const extraWidth = newContainerWidth - totalDefaultWidth;
            const distributedWidth = extraWidth / visibleColumns.length;
            const newSizing: Record<string, number> = {};
            visibleColumns.forEach(col => {
              if (col.id !== 'select') {
                newSizing[col.id] = col.getSize() + distributedWidth;
              }
            });
            table.setColumnSizing(newSizing);
          }
        }
      }
    };

    updateContainerWidth();
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [tableId, table]);

  // Keep the sticky header's horizontal offset in sync with the body's
  // horizontal scroll so the header columns stay aligned with the body cells.
  React.useEffect(() => {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (!body || !header) return;
    const onScroll = () => {
      header.scrollLeft = body.scrollLeft;
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, []);

  const columnSizing = table.getState().columnSizing;
  const columnVisibility = table.getState().columnVisibility;
  const filledColumnWidths = React.useMemo(() => {
    if (!tableId || !containerWidth) return undefined;

    const visibleColumns = table.getVisibleLeafColumns();
    const fillCount = Math.min(fillLastColumns, visibleColumns.length);
    const filledColumns = visibleColumns.slice(-fillCount);
    const otherColumns = visibleColumns.slice(0, -fillCount);

    const otherColumnsWidth = otherColumns.reduce((sum, col) => sum + col.getSize(), 0);
    const remainingWidth = containerWidth - otherColumnsWidth;
    const perColumnWidth = remainingWidth / fillCount;

    const widths = new Map<string, number>();
    filledColumns.forEach(col => {
      const minWidth = col.getSize();
      widths.set(col.id, Math.max(perColumnWidth, minWidth));
    });
    return widths;
  }, [tableId, containerWidth, table, columnSizing, columnVisibility, fillLastColumns]);

  const tableWidth = tableId ? Math.max(table.getTotalSize(), containerWidth) : undefined;
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  const columnWidthStyle = (
    columnId: string,
    fallbackSize: number,
    defaultColumnSize?: number
  ): React.CSSProperties => {
    const isFilled = filledColumnWidths?.has(columnId);
    const filled = filledColumnWidths?.get(columnId);
    if (tableId && isFilled) return { width: filled, maxWidth: filled };
    if (tableId && columnId === 'select' && defaultColumnSize) return { width: defaultColumnSize };
    if (tableId && !isFilled && columnId !== 'select') return { width: fallbackSize };
    if (!tableId) return { width: fallbackSize };
    return {};
  };

  return (
    <div className={cn('flex w-full flex-col', className)} {...props}>
      {/* No overflow-clipping wrapper here: the sticky header below must have
          only `overflow: visible` ancestors up to the page scroll container,
          otherwise it would pin to a wrapper instead of the page. The border
          is split across the header (top/sides) and body (sides/bottom). */}
      <div className="contents">
        {/* Sticky header: a page-level `position: sticky` element that pins
            below the page chrome. Its own horizontal scroll mirrors the body. */}
        <div
          ref={headerScrollRef}
          className="sticky z-10 overflow-x-hidden rounded-t-md border border-b-0 bg-muted shadow-[0_1px_0_0_hsl(var(--border))]"
          style={{ top: stickyHeaderTopPx }}
        >
          <table
            className="w-full caption-bottom text-sm"
            style={tableWidth ? { width: tableWidth, tableLayout: 'fixed' } : undefined}
          >
            <thead className="[&_tr]:border-b">
              {headerGroups.map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      variant={variant}
                      className={cn(
                        'group/header relative',
                        header.column.getIsResizing() && 'select-none'
                      )}
                      style={{
                        ...getCommonPinningStyles({ column: header.column, withBorder: false }),
                        ...columnWidthStyle(
                          header.id,
                          header.getSize(),
                          header.column.columnDef.size
                        ),
                        ...(header.column.columnDef.enableSorting && { cursor: 'pointer' }),
                      }}
                      onClick={() =>
                        header.column.columnDef.enableSorting ? header.column.toggleSorting() : null
                      }
                    >
                      <div className="flex h-full items-center">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.columnDef.enableSorting && (
                          <DataTableSortingArrows isSorted={header.column.getIsSorted()} />
                        )}
                      </div>
                      {tableId &&
                        !filledColumnWidths?.has(header.id) &&
                        header.column.id !== 'select' && (
                          <DataTableResizeHandle header={header} onResizeEnd={onResizeEnd} />
                        )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </thead>
          </table>
        </div>

        {/* Body: horizontally scrollable, with the side + bottom border so the
            whole thing reads as one bordered box with the sticky header. */}
        <div
          ref={bodyScrollRef}
          className="overflow-x-auto rounded-b-md border border-t-0 [scrollbar-width:thin]"
        >
          <div ref={containerRef}>
            <table
              className="w-full caption-bottom text-sm"
              style={tableWidth ? { width: tableWidth, tableLayout: 'fixed' } : undefined}
            >
              <TableBody className="bg-background">
                {rows.map(row => {
                  const customRowClassName = getRowClassName?.(row.original);
                  return (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn(
                        (onRowClick || getRowHref) && 'group cursor-pointer hover:bg-muted/50',
                        customRowClassName
                      )}
                      onContextMenu={
                        onRowContextMenu
                          ? event => onRowContextMenu(row.original, event)
                          : undefined
                      }
                    >
                      {row.getVisibleCells().map(cell => (
                        <TableCell
                          key={cell.id}
                          variant={variant}
                          style={{
                            ...getCommonPinningStyles({ column: cell.column, withBorder: false }),
                            ...columnWidthStyle(
                              cell.column.id,
                              cell.column.getSize(),
                              cell.column.columnDef.size
                            ),
                          }}
                          className={cn(
                            cell.column.columnDef.meta?.cellClassName,
                            customRowClassName
                          )}
                          {...(cell.column.id !== 'select'
                            ? buildRowMouseHandlers({ row: row.original, onRowClick, getRowHref })
                            : {})}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {lastRowChildren}
              </TableBody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
